import { OpenAPI } from "simplydo/interfaces";

import { API, APIError } from ".";
import SearchParams from "../searchParams";

export const ideas = (api: API) => ({
  get(
    id: string,
    success: (data: OpenAPI.GET<"/ideas/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.maybeAuthenticatedRequest("GET", `/ideas/${id}`, null, success, fail);
  },
  logView(
    id: string,
    success: (data: OpenAPI.POST<"/ideas/{id}/view">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", `/ideas/${id}/view`, null, success, fail);
  },
  update(
    id: string,
    data: OpenAPI.PUT<"/ideas/{id}">["requestBody"],
    success: (data: OpenAPI.PUT<"/ideas/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", `/ideas/${id}`, data, success, fail);
  },
  updateStatus(
    id: string,
    data: OpenAPI.PUT<"/ideas/{id}/status">["requestBody"],
    success: (data: OpenAPI.PUT<"/ideas/{id}/status">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", `/ideas/${id}/status`, data, success, fail);
  },
  assign(
    id: string,
    data: OpenAPI.PUT<"/ideas/{id}/assign">["requestBody"],
    success: (data: OpenAPI.PUT<"/ideas/{id}/assign">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("PUT", `/ideas/${id}/assign`, data, success, fail);
  },
  delete(
    id: string,
    success: (data: OpenAPI.DELETE<"/ideas/{id}">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("DELETE", `/ideas/${id}`, null, success, fail);
  },
  bulkDelete(
    ideaIds: string[],
    success: (data: OpenAPI.DELETE<"/ideas/bulk_delete">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      "/ideas/bulk_delete",
      { ideas: ideaIds },
      success,
      fail,
    );
  },
  assigned(
    success: (data: OpenAPI.GET<"/ideas/assigned">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("GET", "/ideas/assigned", null, success, fail);
  },
  addCollaborator(
    id: string,
    userId: string,
    success: (
      data: OpenAPI.PUT<"/ideas/{id}/members/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/ideas/${id}/members/${userId}`,
      null,
      success,
      fail,
    );
  },
  addCollaboratorMulti(
    id: string,
    userIds: string[],
    success: (data: OpenAPI.PUT<"/ideas/{id}/members">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/ideas/${id}/members`,
      { users: userIds },
      success,
      fail,
    );
  },
  removeCollaborator(
    id: string,
    userId: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{id}/members/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${id}/members/${userId}`,
      null,
      success,
      fail,
    );
  },
  updateOwner(
    id: string,
    userId: string,
    success: (
      data: OpenAPI.PUT<"/ideas/{id}/owner/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/ideas/${id}/owner/${userId}`,
      null,
      success,
      fail,
    );
  },
  vote(
    id: string,
    add: boolean,
    success: (data: OpenAPI.POST<"/ideas/{id}/votes">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      add ? "POST" : "DELETE",
      `/ideas/${id}/votes`,
      null,
      success,
      fail,
    );
  },
  getComments(
    id: string,
    params: OpenAPI.GET<"/ideas/{id}/comments">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/ideas/{id}/comments">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const query = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/ideas/${id}/comments${query}`,
      null,
      success,
      fail,
    );
  },
  postComment(
    id: string,
    data: OpenAPI.POST<"/ideas/{id}/comments">["requestBody"],
    success: (data: OpenAPI.POST<"/ideas/{id}/comments">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${id}/comments`,
      data,
      success,
      fail,
    );
  },
  deleteComment(
    id: string,
    commentId: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{id}/comments/{comment_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${id}/comments/${commentId}`,
      null,
      success,
      fail,
    );
  },
  pinComment(
    id: string,
    commentId: string,
    isPinning: boolean,
    success: (
      data: OpenAPI.PUT<"/ideas/{id}/comments/{comment_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/ideas/${id}/comments/${commentId}`,
      { isPinned: !!isPinning },
      success,
      fail,
    );
  },
  createAttachment(
    id: string,
    data: OpenAPI.POST<"/ideas/{id}/attachments">["requestBody"],
    success: (
      data: OpenAPI.POST<"/ideas/{id}/attachments">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${id}/attachments`,
      data,
      success,
      fail,
    );
  },
  createMultipleAttachments(
    id: string,
    data: OpenAPI.POST<"/ideas/{id}/attachments/multiple">["requestBody"],
    success: (
      data: OpenAPI.POST<"/ideas/{id}/attachments/multiple">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${id}/attachments/multiple`,
      data,
      success,
      fail,
    );
  },
  deleteAttachment(
    id: string,
    uuid: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{id}/attachments/{uuid}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${id}/attachments/${uuid}`,
      null,
      success,
      fail,
    );
  },
  moveToChallenge(
    ideaIds: string[],
    challengeId: string,
    success: (
      data: OpenAPI.PUT<"/ideas/challenges/{challenge_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const params = new SearchParams({ ideas: ideaIds });
    api.authenticatedRequest(
      "PUT",
      `/ideas/challenges/${challengeId}${params.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getAssessment(
    id: string,
    assessmentRoundId: string,
    success: (
      data: OpenAPI.GET<"/ideas/{id}/assessment/{assessment_round_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${id}/assessment/${assessmentRoundId}`,
      null,
      success,
      fail,
    );
  },
  updateAssessment(
    id: string,
    assessmentRoundId: string,
    data: OpenAPI.PUT<"/ideas/{id}/assessment/{assessment_round_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/ideas/{id}/assessment/{assessment_round_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/ideas/${id}/assessment/${assessmentRoundId}`,
      data,
      success,
      fail,
    );
  },
  submitAssessment(
    id: string,
    assessmentRoundId: string,
    success: (
      data: OpenAPI.POST<"/ideas/{id}/assessment/{assessment_round_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${id}/assessment/${assessmentRoundId}`,
      null,
      success,
      fail,
    );
  },
  unsubmitAssessment(
    id: string,
    assessmentRoundId: string,
    userId: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{id}/assessments/{assessment_round_id}/{user_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${id}/assessments/${assessmentRoundId}/${userId}`,
      null,
      success,
      fail,
    );
  },
  commentAddReaction(
    id: string,
    commentId: string,
    emoji: string,
    success: (
      data: OpenAPI.PUT<"/ideas/{id}/comments/{comment_id}/reacts/{emoji}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/ideas/${id}/comments/${commentId}/reacts/${emoji}`,
      null,
      success,
      fail,
    );
  },
  commentRemoveReaction(
    id: string,
    commentId: string,
    emoji: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{id}/comments/{comment_id}/reacts/{emoji}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${id}/comments/${commentId}/reacts/${emoji}`,
      null,
      success,
      fail,
    );
  },
  follow(
    id: string,
    success: (data: OpenAPI.POST<"/ideas/{id}/followers">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${id}/followers`,
      null,
      success,
      fail,
    );
  },
  unfollow(
    id: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{id}/followers">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${id}/followers`,
      null,
      success,
      fail,
    );
  },
  followers(
    id: string,
    params: OpenAPI.GET<"/ideas/{id}/followers">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/ideas/{id}/followers">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    const queryParams = new SearchParams(params).toString();
    api.authenticatedRequest(
      "GET",
      `/ideas/${id}/followers?${queryParams}`,
      null,
      success,
      fail,
    );
  },
  merge(
    data: OpenAPI.POST<"/ideas/merge">["requestBody"],
    success: (data: OpenAPI.POST<"/ideas/merge">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", "/ideas/merge", data, success, fail);
  },
  updateMergedIdea(
    id: string,
    data: OpenAPI.POST<"/ideas/{id}/merge">["requestBody"],
    success: (data: OpenAPI.POST<"/ideas/{id}/merge">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", `/ideas/${id}/merge`, data, success, fail);
  },
  removeIdeaFromMerge(
    parentId: string,
    childId: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{parent_id}/merge/{child_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${parentId}/merge/${childId}`,
      null,
      success,
      fail,
    );
  },
  createIdeaNameWithAI(
    id: string,
    success: (data: OpenAPI.GET<"/ideas/{id}/ai/name">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${id}/ai/name`,
      null,
      success,
      fail,
    );
  },
  getAiIdeaFeedback(
    id: string,
    success: (data: OpenAPI.GET<"/ideas/{id}/ai/feedback">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${id}/ai/feedback`,
      null,
      success,
      fail,
    );
  },
  getAiIdeaAnonymityCheck(
    id: string,
    success: (
      data: OpenAPI.GET<"/ideas/{id}/ai/anonymity-check">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${id}/ai/anonymity-check`,
      null,
      success,
      fail,
    );
  },
  importFromWord(
    id: string,
    data: OpenAPI.POST<"/ideas/{id}/import/word">["requestBody"],
    success: (
      data: OpenAPI.POST<"/ideas/{id}/import/word">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${id}/import/word`,
      data,
      success,
      fail,
    );
  },
  getCopySuggestions(
    ideaId: string,
    fieldType: string,
    params: OpenAPI.GET<"/ideas/{idea_id}/copy/suggest/{field_type}">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/ideas/{idea_id}/copy/suggest/{field_type}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const search = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/ideas/${ideaId}/copy/suggest/${fieldType}${search.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  copyData(
    fromIdea: string,
    fromField: string,
    toIdea: string,
    toField: string,
    params: OpenAPI.GET<"/ideas/{from_idea}/{from_field}/copyTo/{to_idea}/{to_field}">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/ideas/{from_idea}/{from_field}/copyTo/{to_idea}/{to_field}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    const search = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/ideas/${fromIdea}/${fromField}/copyTo/${toIdea}/${toField}${search.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  setDeadline(
    data: OpenAPI.POST<"/ideas/deadline">["requestBody"],
    success: (data: OpenAPI.POST<"/ideas/deadline">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest("POST", `/ideas/deadline`, data, success, fail);
  },
  removeDeadline(
    ideaIds: string[],
    success: (data: OpenAPI.DELETE<"/ideas/deadline">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/deadline`,
      { ideaIds },
      success,
      fail,
    );
  },
  getImpactStatements(
    ideaId: string,
    success: (
      data: OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${ideaId}/impactStatement`,
      null,
      success,
      fail,
    );
  },
  getImpactStatement(
    ideaId: string,
    statementId: string,
    success: (
      data: OpenAPI.GET<"/ideas/{idea_id}/impactStatement/{statement_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${ideaId}/impactStatement/${statementId}`,
      null,
      success,
      fail,
    );
  },
  updateImpactStatement(
    ideaId: string,
    statementId: string,
    data: OpenAPI.PUT<"/ideas/{idea_id}/impactStatement/{statement_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/ideas/{idea_id}/impactStatement/{statement_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/ideas/${ideaId}/impactStatement/${statementId}`,
      data,
      success,
      fail,
    );
  },
  createImpactStatement(
    ideaId: string,
    data: OpenAPI.POST<"/ideas/{idea_id}/impactStatement">["requestBody"],
    success: (
      data: OpenAPI.POST<"/ideas/{idea_id}/impactStatement">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${ideaId}/impactStatement`,
      data,
      success,
      fail,
    );
  },
  deleteImpactStatement(
    ideaId: string,
    statementId: string,
    success: (
      data: OpenAPI.DELETE<"/ideas/{idea_id}/impactStatement/{statement_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${ideaId}/impactStatement/${statementId}`,
      null,
      success,
      fail,
    );
  },
  getFinalOutcomeByToken(
    token: string,
    success: (
      data: OpenAPI.GET<"/ideas/final-outcome/{token}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "GET",
      `/ideas/final-outcome/${encodeURIComponent(token)}`,
      null,
      success,
      fail,
    );
  },
  saveFinalOutcomeByToken(
    token: string,
    data: OpenAPI.PUT<"/ideas/final-outcome/{token}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/ideas/final-outcome/{token}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.unauthenticatedRequest(
      "PUT",
      `/ideas/final-outcome/${encodeURIComponent(token)}`,
      data,
      success,
      fail,
    );
  },
  getFinalOutcomePrompts(
    ideaId: string,
    success: (
      data: OpenAPI.GET<"/ideas/{idea_id}/finalOutcome/prompt">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${ideaId}/finalOutcome/prompt`,
      null,
      success,
      fail,
    );
  },
  promptFinalOutcome(
    ideaId: string,
    data: OpenAPI.POST<"/ideas/{idea_id}/finalOutcome/prompt">["requestBody"],
    success: (
      data: OpenAPI.POST<"/ideas/{idea_id}/finalOutcome/prompt">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${ideaId}/finalOutcome/prompt`,
      data,
      success,
      fail,
    );
  },
  removeFinalOutcomePrompt(
    ideaId: string,
    data: OpenAPI.DELETE<"/ideas/{idea_id}/finalOutcome/prompt">["requestBody"],
    success: (
      data: OpenAPI.DELETE<"/ideas/{idea_id}/finalOutcome/prompt">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/ideas/${ideaId}/finalOutcome/prompt`,
      data,
      success,
      fail,
    );
  },
  sendEmail(
    ideaId: string,
    data: OpenAPI.POST<"/ideas/{idea_id}/email">["requestBody"],
    success: (data: OpenAPI.POST<"/ideas/{idea_id}/email">["response"]) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${ideaId}/email`,
      data,
      success,
      fail,
    );
  },
  previewAssessmentFeedback(
    ideaId: string,
    data: OpenAPI.POST<"/ideas/{idea_id}/assessmentFeedback/preview">["requestBody"],
    success: (
      data: OpenAPI.POST<"/ideas/{idea_id}/assessmentFeedback/preview">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${ideaId}/assessmentFeedback/preview`,
      data,
      success,
      fail,
    );
  },
  sendAssessmentFeedback(
    ideaId: string,
    data: OpenAPI.POST<"/ideas/{idea_id}/assessmentFeedback/send">["requestBody"],
    success: (
      data: OpenAPI.POST<"/ideas/{idea_id}/assessmentFeedback/send">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "POST",
      `/ideas/${ideaId}/assessmentFeedback/send`,
      data,
      success,
      fail,
    );
  },
  getEmailLogs(
    ideaId: string,
    page: number,
    success: (
      data: OpenAPI.GET<"/ideas/{idea_id}/email/logs">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${ideaId}/email/logs?page=${page}`,
      null,
      success,
      fail,
    );
  },
  getEmailLog(
    ideaId: string,
    logId: string,
    success: (
      data: OpenAPI.GET<"/ideas/{idea_id}/email/logs/{log_id}">["response"],
    ) => void,
    fail: (error: APIError) => void,
  ) {
    api.authenticatedRequest(
      "GET",
      `/ideas/${ideaId}/email/logs/${logId}`,
      null,
      success,
      fail,
    );
  },
});
